import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Layout } from '../components/Layout.tsx';
import { apiClient } from '../api/client.ts';
import { ChevronDown, Loader2, Search, X } from 'lucide-react';

type BulkReportRow = {
  id: string;
  ideaId: string;
  ideaName: string;
  ideaStatus: string;
  action: string;
  createdAt: string;
};

const IDEA_STATUS_LABELS: Record<string, string> = {
  SUBMITTED: 'Yet to Be Reviewed',
  UNDER_REVIEW: 'Reviewed',
  WORK_IN_PROGRESS: 'Work in Progress',
  PENDING_HEAD_APPROVAL: 'Pending IC Head Approval',
  PENDING_EXTERNAL_APPROVAL: 'Requested EC Head Approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  MORE_INFO_REQUESTED: 'Hold',
  DUPLICATE: 'Duplicate',
};

function statusLabel(value: string | null | undefined) {
  if (!value) return '—';
  return IDEA_STATUS_LABELS[value] || value.replaceAll('_', ' ');
}

function ticketId(ideaId: string) {
  return `CG${ideaId.substring(0, 7).toUpperCase()}`;
}

export function BulkReportPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [notice, setNotice] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['bulkReportTimelines'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/ideas/bulk-report');
      return res.data.data as BulkReportRow[];
    },
  });

  const rows = data ?? [];

  const actionOptions = useMemo(
    () => Array.from(new Set(rows.map((row) => row.action))).sort(),
    [rows]
  );
  const statusOptions = useMemo(
    () => Array.from(new Set(rows.map((row) => row.ideaStatus).filter(Boolean))).sort(),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (actionFilter !== 'ALL' && row.action !== actionFilter) return false;
      if (statusFilter !== 'ALL' && row.ideaStatus !== statusFilter) return false;
      if (!q) return true;
      return (
        ticketId(row.ideaId).toLowerCase().includes(q) ||
        row.ideaName.toLowerCase().includes(q) ||
        row.action.toLowerCase().includes(q) ||
        statusLabel(row.ideaStatus).toLowerCase().includes(q)
      );
    });
  }, [rows, search, actionFilter, statusFilter]);

  const filteredIds = filteredRows.map((row) => row.id);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedIds.includes(id));
  const selectedRows = rows.filter((row) => selectedIds.includes(row.id));
  const canUpdate = selectedRows.some((row) => row.action === 'ADMIN_APPROVED' || row.action === 'EVALUATED');
  const canRevert = selectedRows.some((row) => row.action === 'EVALUATED' || row.action === 'COMMITTEE_HEAD_EVALUATED');

  const actionMutation = useMutation({
    mutationFn: async ({ ids, mode }: { ids: string[]; mode: 'update' | 'revert' }) => {
      const res = await apiClient.post('/admin/ideas/bulk-report/actions', { ids, mode });
      return res.data.data as { updatedCount: number };
    },
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['bulkReportTimelines'] });
      setSelectedIds([]);
      setNotice(
        variables.mode === 'update'
          ? `${result.updatedCount} row${result.updatedCount === 1 ? '' : 's'} set to COMMITTEE_HEAD_EVALUATED.`
          : `${result.updatedCount} row${result.updatedCount === 1 ? '' : 's'} reverted to ADMIN_APPROVED.`
      );
    },
  });

  const toggleAll = () => {
    if (allFilteredSelected) {
      setSelectedIds((current) => current.filter((id) => !filteredIds.includes(id)));
      return;
    }
    setSelectedIds((current) => Array.from(new Set([...current, ...filteredIds])));
  };

  const toggleOne = (id: string) => {
    setSelectedIds((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
  };

  const runAction = (mode: 'update' | 'revert') => {
    const ids = selectedRows
      .filter((row) =>
        mode === 'update'
          ? row.action === 'ADMIN_APPROVED' || row.action === 'EVALUATED'
          : row.action === 'EVALUATED' || row.action === 'COMMITTEE_HEAD_EVALUATED'
      )
      .map((row) => row.id);
    if (ids.length === 0) return;
    setNotice('');
    actionMutation.mutate({ ids, mode });
  };

  return (
    <Layout title="Bulk Report">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px] flex-1 max-w-sm">
          <div className="flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 shadow-sm">
            <Search className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by idea name or ticket id"
              className="min-w-0 flex-1 bg-transparent text-sm text-slate-800 placeholder:text-slate-400 outline-none"
            />
            {search ? (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                title="Clear"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            ) : null}
          </div>
        </div>
        <div className="relative">
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="h-10 min-w-[180px] appearance-none rounded-xl border border-slate-200 bg-white pl-3 pr-9 text-xs font-semibold text-slate-700 shadow-sm outline-none"
            aria-label="Filter by action"
          >
            <option value="ALL">All Actions</option>
            {actionOptions.map((action) => (
              <option key={action} value={action}>
                {action.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        </div>
        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-10 min-w-[180px] appearance-none rounded-xl border border-slate-200 bg-white pl-3 pr-9 text-xs font-semibold text-slate-700 shadow-sm outline-none"
            aria-label="Filter by idea status"
          >
            <option value="ALL">All Statuses</option>
            {statusOptions.map((status) => (
              <option key={status} value={status}>
                {statusLabel(status)}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        </div>
        <div className="flex h-10 items-center rounded-xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 shadow-sm whitespace-nowrap">
          {filteredRows.length === rows.length
            ? `Total ${rows.length}`
            : `Showing ${filteredRows.length} of ${rows.length}`}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            disabled={!canUpdate || actionMutation.isPending}
            onClick={() => runAction('update')}
            className="h-10 px-4 rounded-xl text-xs font-bold border cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 bg-slate-900 text-white border-slate-900"
          >
            Update
          </button>
          <button
            type="button"
            disabled={!canRevert || actionMutation.isPending}
            onClick={() => runAction('revert')}
            className="h-10 px-4 rounded-xl text-xs font-bold border cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
          >
            Revert
          </button>
        </div>
      </div>

      {notice ? <p className="mb-3 text-sm font-medium text-emerald-700">{notice}</p> : null}
      {actionMutation.isError ? (
        <p className="mb-3 text-sm font-medium text-red-600">Could not update the selected rows.</p>
      ) : null}

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="w-14 px-4 py-3 text-center align-middle">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={toggleAll}
                    aria-label="Select all rows"
                    className="h-4 w-4 rounded border-slate-300 align-middle"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider align-middle">Ticket ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider align-middle">Idea Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider align-middle">Idea Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider align-middle">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading timelines...
                  </td>
                </tr>
              ) : isError ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-red-500">
                    Failed to load bulk report rows.
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                    No matching rows found.
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="w-14 px-4 py-3 text-center align-middle">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(row.id)}
                        onChange={() => toggleOne(row.id)}
                        aria-label={`Select ${row.ideaName}`}
                        className="h-4 w-4 rounded border-slate-300 align-middle"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 font-mono align-middle whitespace-nowrap">{ticketId(row.ideaId)}</td>
                    <td className="px-4 py-3 text-sm text-slate-800 font-medium align-middle">{row.ideaName}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 align-middle whitespace-nowrap">{statusLabel(row.ideaStatus)}</td>
                    <td className="px-4 py-3 text-sm align-middle">
                      <span
                        className={`inline-flex items-center whitespace-nowrap px-2.5 py-1 rounded-full text-xs font-medium leading-none ${
                          row.action === 'ADMIN_APPROVED'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {row.action.replaceAll('_', ' ')}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
